import graphqlClient from "@/GQLClient";
import {
  GetSummarizedMeetingsOnMyInstitutionDocument,
  type SummarizedMeetingsFragment,
  type EntityPagerFilter,
  DeleteMeetingDocument,
  GetInternalMeetingsOnMyInstitutionDocument,
  type MeetingFragment,
  type MeetingInput,
  SaveMeetingDocument,
  Delete_Type,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { IPagerResult } from "@/interfaces";
import type { IdType } from "@/types";

const getMeetings = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedMeetingsFragment>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedMeetingsOnMyInstitutionDocument,
    variables: {
      filter,
    },
  });

  if (response.data.institutions?.getMyInstitution.meetings) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.meetings;
    return {
      edges: edges.map((item) => item.node as SummarizedMeetingsFragment),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getMeetingDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetInternalMeetingsOnMyInstitutionDocument,
    variables: {
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.meetings) {
    const { edges } = response.data.institutions.getMyInstitution.meetings;
    return edges.map((item) => item.node as MeetingFragment).at(0);
  }

  throw new SentryGraphQLError();
};

const save = async (meeting: MeetingInput) => {
  const response = await graphqlClient.mutate({
    mutation: SaveMeetingDocument,
    variables: {
      meeting,
    },
  });

  if (response.data?.meeting?.save?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

const deleteItem = async (id: string, type: Delete_Type) => {
  const response = await graphqlClient.mutate({
    mutation: DeleteMeetingDocument,
    variables: {
      id,
      type,
    },
  });

  if (response.data?.meeting?.delete?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

export default {
  getMeetings,
  getMeetingDetail,
  save,
  deleteItem,
};
